import { useEffect } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  BEAT_HR_TAB_ID,
  DEFAULT_CONTROL_MODAL_VALUE,
  selectControlModal,
  selectEventNews,
  useReportStore,
} from '@/entities/report';

/**
 * Wires the add-new-event button on the beats tab together with its keyboard shortcut and the count of pending new events. Exposes a click handler that suppresses the originating event and opens the add new event modal only when closed, and registers a window keydown listener firing it on the W key while the beats tab zone is hovered, ignoring auto-repeat and Ctrl or Meta combinations.
 *
 * @returns An object with the number of unsaved new events, the add-new-event click handler, and the control modal setter.
 */
const useButtonAddNewEvent = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the beats tab modal control flags into the report store. Forwards the value to the shared setControlModal action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the beats tab modal control flags from the report store without subscribing. Applies the tab-keyed control modal selector to the current store snapshot, so click and key handlers see the flags as they are right now.
   *
   * @returns The current control modal flag set for the beats tab.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());

  const eventLength = eventNews.length;

  /**
   * Opens the add new event modal on the beats tab. Stops propagation and the default action on the originating event when one is passed, then writes the default control modal value with isShowAddNewEvent enabled only if that modal is not already open.
   *
   * @param e - Optional originating click or key event whose propagation and default action are suppressed.
   * @returns Nothing.
   */
  const onClickAddNewEvent = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!getControlModal().isShowAddNewEvent) {
      setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowAddNewEvent: true });
    }
  };

  useEffect(() => {
    /**
     * Triggers the add-new-event shortcut from a window keydown. Ignores auto-repeat, presses made while the beats tab zone is not hovered, and any Ctrl or Meta combination, then opens the add new event modal on key code 87, the W key.
     *
     * @param event - Keydown event carrying the key code and the modifier flags.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      if (keyCode === 87) {
        // W
        onClickAddNewEvent();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    eventLength,
    onClickAddNewEvent,
    setControlModal,
  };
};

export default useButtonAddNewEvent;
